import styled from '@emotion/styled';
import type {
  UserHelpRequestMessage,
  UserHelpRequestPrototype,
  UserHelpRequest as UserHelpRequestType,
} from '@racemap/sdk/schema/userHelpRequest.js';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { Flex, Input, Modal, Tabs, message } from 'antd';
import { useState } from 'react';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import { UAParser } from 'ua-parser-js';
import { MarkdownPreview } from './BasicComponents/MarkdownPreview';
import { UserLabel } from './BasicComponents/UserLabel/index.js';

const { TextArea } = Input;
const apiClient = RacemapAPIClient.fromWindowLocation();

export const AiChatHistory = ({ messages }: { messages: Array<UserHelpRequestMessage> }) => (
  <>
    {messages.length > 0 ? (
      messages.map((aiMessage, index) => (
        <div
          key={`${aiMessage.turn}-${aiMessage.role}-${index}`}
          style={{ marginBottom: index === messages.length - 1 ? 0 : 12 }}
        >
          <div
            style={{
              fontSize: '12px',
              color: '#888',
              fontWeight: 600,
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            {aiMessage.role}
          </div>
          <MarkdownPreview markdown={aiMessage.content} />
        </div>
      ))
    ) : (
      <div style={{ fontSize: '13px', color: '#666' }}>
        Ask your question. Our AI knows our documentation and can help you find the answer or guide
        you through the next steps.
      </div>
    )}
  </>
);

export const UserHelpRequest = () => {
  const [visible, setVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('ai');
  const [content, setContent] = useState('');
  const [aIPrompt, setAIPrompt] = useState('');
  const [activeAiRequest, setActiveAiRequest] = useState<UserHelpRequestType | null>(null);
  const [loading, setLoading] = useState(false);

  const currentUser = useCurrentUser();
  const userAgent = new UAParser().getResult();
  const isInIframe = window.self !== window.top;
  const referrer = document.referrer || '';

  // Don't show help request button if in an iframe
  if (isInIframe && referrer.includes('https://go.racemap.com')) return null;

  // Don't show help request button if the user is not logged in
  if (currentUser === null) return null;

  const getSystemInfo = (): UserHelpRequestPrototype['systemInfo'] => ({
    browser: `${userAgent.browser.name} ${userAgent.browser.version}`,
    os: `${userAgent.os.name} ${userAgent.os.version}`,
    engine: `${userAgent.engine.name} ${userAgent.engine.version}`,
    device: userAgent.device.type || 'desktop', // can be 'mobile', 'tablet', etc.
    userAgent: navigator.userAgent,
    url: window.location.href,
    timestamp: new Date(),
  });

  const handleSend = async () => {
    if (!content.trim()) {
      message.warning('Please enter some feedback.');
      return;
    }

    setLoading(true);
    try {
      const helpRequest: UserHelpRequestPrototype = {
        content,
        source: 'frontend',
        type: 'MailRequest',
        systemInfo: getSystemInfo(),
      };

      await apiClient.createUserHelpRequest({ helpRequest });
      message.success('Help request sent. Thank you!');
      setContent('');
      setVisible(false);
    } catch (err) {
      console.error(err);
      message.error('Failed to send help request.');
    } finally {
      setLoading(false);
    }
  };

  const handleAiSend = async () => {
    if (!aIPrompt.trim()) {
      message.warning('Please enter a message for AI support.');
      return;
    }

    setLoading(true);
    try {
      const answer =
        activeAiRequest == null
          ? await apiClient.createUserHelpRequest({
              helpRequest: {
                content: aIPrompt,
                source: 'frontend',
                type: 'AIRequest',
                systemInfo: getSystemInfo(),
              },
            })
          : await apiClient.updateUserHelpRequest({
              userHelpRequestId: activeAiRequest.id,
              patch: {
                content: aIPrompt,
                systemInfo: getSystemInfo(),
              },
            });

      setActiveAiRequest(answer);
      setAIPrompt('');
      message.success('AI support request sent.');
    } catch (err) {
      console.error(err);
      message.error('Failed to send AI support request.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <HelpTab
        onClick={() => {
          setVisible(true);
          setActiveTab('ai');
        }}
      >
        Get Help
      </HelpTab>
      <Modal
        title={
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
              <Flex gap={4}>
                Hey <UserLabel userId={currentUser?.id} />. How can we help?
              </Flex>
            </div>
            <div style={{ fontSize: '13px', color: '#888' }}>
              Tell us where you struggled or got blocked — no detail is too small.
            </div>
          </div>
        }
        open={visible}
        onCancel={() => setVisible(false)}
        onOk={activeTab === 'email' ? handleSend : handleAiSend}
        okText={activeTab === 'email' ? 'Send Email' : 'Send to AI'}
        confirmLoading={loading}
        width={624}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'ai',
              label: 'AI support',
              children: (
                <div style={{ height: 300, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      border: '1px solid #f0f0f0',
                      borderRadius: 8,
                      padding: 12,
                      background: '#fafafa',
                      overflowY: 'auto',
                    }}
                  >
                    <AiChatHistory messages={activeAiRequest?.messages ?? []} />
                  </div>
                  <TextArea
                    rows={2}
                    value={aIPrompt}
                    onChange={(e) => setAIPrompt(e.target.value)}
                    placeholder="Ask a question…"
                    autoFocus={activeTab === 'ai'}
                  />
                  <div style={{ fontSize: '11px', color: '#aaa', marginTop: -4 }}>
                    Feel free to write in any language — our AI understands you.
                  </div>
                </div>
              ),
            },
            {
              key: 'email',
              label: 'EMail us',
              children: (
                <div style={{ height: 300, display: 'flex', flexDirection: 'column' }}>
                  <TextArea
                    style={{ flex: 1, resize: 'none' }}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    placeholder="Tell us where you struggled or got blocked..."
                    autoFocus={activeTab === 'email'}
                  />
                </div>
              ),
            },
          ]}
        />
      </Modal>
    </>
  );
};

const HelpTab = styled.div`
  position: fixed;
  right: 0;
  bottom: 40px;
  background: ${RacemapColors.PaleBlue};
  color: #fff;
  padding: 8px 4px;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  border-radius: 0 4px 4px 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  user-select: none;
  z-index: 1000;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: -60%;
    left: -75%;
    width: 50%;
    height: 220%;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.45) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: skewX(-15deg);
    animation: helpTabShine 10s ease-in-out infinite;
  }

  @keyframes helpTabShine {
    0%, 70% {
      left: -75%;
    }
    80%, 100% {
      left: 150%;
    }
  }

  &:hover {
    background: ${RacemapColors.DarkBlue};
  }
`;
